import * as Sentry from "@sentry/react";
import type { ErrorEvent } from "@sentry/react";
import type { GraphqlNetworkFailure } from "@ths/shared/observability";

const SENSITIVE_EXACT_KEYS = new Set([
  "accesscode",
  "basecamptoken",
  "buildingaccesscode",
  "carmake",
  "carmodel",
  "carplatenumber",
  "code",
  "contactemail",
  "contactphone",
  "currentaccesscode",
  "dodid",
  "dodnumber",
  "edipi",
  "email",
  "firstname",
  "guestfirstname",
  "guestfullname",
  "guestlastname",
  "guestname",
  "lastname",
  "licenseplate",
  "phone",
  "platenumber",
  "roomaccesscode",
  "selfcheckintoken",
  "vehiclecolor",
  "vehiclemake",
  "vehiclemodel",
  "verificationtoken",
]);

const SENSITIVE_KEY_PATTERNS = [
  "token",
  "password",
  "secret",
  "apikey",
  "api_key",
  "authorization",
  "credential",
  "session",
  "cookie",
];

const SENSITIVE_QUERY_PARAM_PATTERN =
  /([?&])(token|access_token|api_key|apikey|auth|session|sessionToken|session_token|jwt|basecampToken|basecamp_token|verificationToken|verification_token|edipi|email|phone)=[^&]*/gi;

function isSensitiveKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  const normalizedKey = lowerKey.replace(/[_-]/g, "");

  return (
    SENSITIVE_EXACT_KEYS.has(normalizedKey) ||
    SENSITIVE_KEY_PATTERNS.some((pattern) => lowerKey.includes(pattern))
  );
}

function normalizeError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

export function redactBasecampUrl(url: string | undefined): string | undefined {
  if (!url) return url;

  return url.replace(SENSITIVE_QUERY_PARAM_PATTERN, "$1$2=[REDACTED]");
}

export function sanitizeBasecampSentryData<T>(value: T, depth = 0): T {
  if (depth > 10 || value == null) return value;

  if (Array.isArray(value)) {
    return value.map((item) =>
      sanitizeBasecampSentryData(item, depth + 1),
    ) as T;
  }

  if (typeof value !== "object") return value;

  const redacted: Record<string, unknown> = {};
  for (const [key, nestedValue] of Object.entries(value)) {
    if (isSensitiveKey(key)) {
      redacted[key] = "[REDACTED]";
    } else {
      redacted[key] = sanitizeBasecampSentryData(nestedValue, depth + 1);
    }
  }

  return redacted as T;
}

export function sanitizeBasecampSentryEvent(event: ErrorEvent): ErrorEvent {
  if (event.request?.headers) {
    const headers = event.request.headers;
    delete headers.Authorization;
    delete headers.Cookie;
    delete headers["X-Auth-Token"];
    delete headers["X-Api-Key"];
    delete headers["X-Access-Token"];
  }

  if (event.request?.url) {
    event.request.url = redactBasecampUrl(event.request.url);
  }

  if (event.contexts) {
    event.contexts = sanitizeBasecampSentryData(event.contexts);
  }

  if (event.extra) {
    event.extra = sanitizeBasecampSentryData(event.extra);
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) =>
      sanitizeBasecampSentryData(breadcrumb),
    );
  }

  return event;
}

export function initBasecampSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) return;

  const environment =
    import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.VERCEL_ENV;
  const release = import.meta.env.VITE_SENTRY_RELEASE;
  const graphqlApiUrl =
    import.meta.env.VITE_APP_GRAPHQL_API_URL ?? "http://localhost:4000";

  Sentry.init({
    dsn,
    environment,
    release: release ? `client-basecamp@${release}` : undefined,
    enabled: environment !== "development" && environment !== "local",
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration()],
    tracePropagationTargets: [new URL(graphqlApiUrl).origin],
    beforeSend: sanitizeBasecampSentryEvent,
    tracesSampleRate: environment === "production" ? 0.1 : 0,
    ignoreErrors: [
      "NetworkError",
      "Network request failed",
      "Failed to fetch",
      "Load failed",
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
    ],
  });
}

export function reportGraphqlNetworkFailure(
  failure: GraphqlNetworkFailure,
): void {
  if (failure.online === false) {
    Sentry.addBreadcrumb({
      category: "graphql-network",
      message: "GraphQL request failed while the browser was offline",
      level: "info",
      data: {
        operationName: failure.operationName,
        attemptCount: failure.attemptCount,
      },
    });
    return;
  }

  const error = new Error(
    `GraphQL network request failed: ${failure.operationName}`,
  );
  error.name = "GraphqlNetworkFailure";

  Sentry.captureException(error, {
    tags: {
      app: "basecamp-app",
      errorType: "graphql-network",
      operationName: failure.operationName,
      networkCategory: failure.category,
      httpStatus: failure.httpStatus ?? "none",
      client_request_id: failure.clientRequestId,
      request_id: failure.serverRequestId ?? "none",
    },
    contexts: { graphqlNetwork: { ...failure } },
    fingerprint: [
      "graphql-network",
      "basecamp-app",
      failure.operationName,
      failure.category,
      String(failure.httpStatus ?? "none"),
    ],
  });
}

export function reportBasecampError(
  error: unknown,
  context: Record<string, unknown> = {},
): void {
  Sentry.withScope((scope) => {
    scope.setTag("app", "client-basecamp");
    scope.setContext(
      "basecamp",
      sanitizeBasecampSentryData({
        surface: "self-service-pwa",
        ...context,
      }),
    );
    Sentry.captureException(normalizeError(error));
  });
}
